(function () {
  "use strict";

  /**
   * ==============================
   * Helpers
   * ==============================
   */
  function esc(str) {
    return String(str == null ? "" : str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function getLocale() {
    return (document.documentElement.getAttribute("lang") || "fr").toLowerCase();
  }

  function toNumber(val) {
    const v = String(val || "").trim().replace(/\s+/g, "").replace(",", ".");
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  }

  function getCellValue(row, colIndex) {
    return ((row.children[colIndex] && row.children[colIndex].textContent) || "").trim();
  }

  function clearSortState(headers) {
    headers.forEach((h) => {
      h.classList.remove("is-sorted-asc", "is-sorted-desc");
      delete h.dataset.sortDir;
    });
  }

  function setHeaderState(headers, activeTh, dir) {
    clearSortState(headers);
    activeTh.dataset.sortDir = dir;
    activeTh.classList.add(dir === "asc" ? "is-sorted-asc" : "is-sorted-desc");
  }

  function sortTableByTh(th, forceAsc) {
    const table = th.closest(".spa-table");
    if (!table) return;

    const tbody = table.querySelector("tbody");
    if (!tbody) return;

    const headers = Array.from(th.parentNode.children);
    const colIndex = headers.indexOf(th);

    const currentlySorted = th.dataset.sortDir; // "asc" | "desc" | undefined
    let nextDir = "asc";

    if (forceAsc) {
      nextDir = "asc";
    } else if (currentlySorted) {
      nextDir = currentlySorted === "asc" ? "desc" : "asc";
    }

    const sortType = th.dataset.sortType || "text";
    const rows = Array.from(tbody.querySelectorAll("tr"));
    const locale = getLocale();

    rows.sort((a, b) => {
      const aVal = getCellValue(a, colIndex);
      const bVal = getCellValue(b, colIndex);

      if (sortType === "number") {
        const aNum = toNumber(aVal);
        const bNum = toNumber(bVal);

        if (aNum === null && bNum === null) return 0;
        if (aNum === null) return 1;
        if (bNum === null) return -1;

        return nextDir === "asc" ? aNum - bNum : bNum - aNum;
      }

      const cmp = aVal.localeCompare(bVal, locale, { sensitivity: "base" });
      return nextDir === "asc" ? cmp : -cmp;
    });

    tbody.innerHTML = "";
    rows.forEach((r) => tbody.appendChild(r));

    setHeaderState(headers, th, nextDir);
  }

  function applyDefaultSort(scopeEl) {
    const tables = scopeEl.querySelectorAll(".spa-table");
    tables.forEach((table) => {
      const firstTh = table.querySelector("thead th");
      if (!firstTh) return;
      sortTableByTh(firstTh, true);
    });
  }

  function parseListTextValue(rawValue) {
    const v = String(rawValue || "").trim();
    if (!v) return [];

    // Cas list.single_line_text_field souvent sérialisé JSON
    if (v.startsWith("[") && v.endsWith("]")) {
      try {
        const arr = JSON.parse(v);
        if (Array.isArray(arr)) {
          return arr.map((x) => String(x || "").trim()).filter(Boolean);
        }
      } catch (e) {
        // fallback plus bas
      }
    }

    // Fallback chaîne simple / séparateurs
    return v
      .split(/[,;|]/g)
      .map((x) => x.trim())
      .filter(Boolean);
  }

  function getI18n(root) {
    const d = (root && root.dataset) || {};
    return {
      noData: d.i18nNoData || "No dimensions found.",
      loading: d.i18nLoading || "Loading dimensions…",
      searchPlaceholder: d.i18nSearchPlaceholder || "Search a model...",
      colModel: d.i18nColModel || "Model",
      colLength: d.i18nColLength || "Length",
      colWidth: d.i18nColWidth || "Width",
      colRadius: d.i18nColRadius || "Radius",
      colSkirt: d.i18nColSkirt || "Skirt",
      colShape: d.i18nColShape || "Shape",
      configMissing: d.i18nConfigMissing || "Missing configuration: shop domain or Storefront token.",
      loadError: d.i18nLoadError || "Unable to load dimensions at the moment."
    };
  }

  /**
   * ==============================
   * Language / Query builder
   * ==============================
   */
  function getShopifyLanguageCode() {
    // ex: "en", "en-CA", "fr-CA"
    const raw = (document.documentElement.getAttribute("lang") || "fr").toLowerCase();
    const short = raw.split("-")[0];

    // Ajuste ici si tu actives d'autres langues
    if (short === "en") return "EN";
    return "FR";
  }

  function buildSpaDimensionsQuery(languageCode) {
    return `
      query SpaDimensionsPage($first: Int!, $after: String) @inContext(language: ${languageCode}) {
        metaobjects(type: "spa_dimensions", first: $first, after: $after, sortKey: "id") {
          pageInfo {
            hasNextPage
            endCursor
          }
          nodes {
            id

            modele: field(key: "modele") { value }
            longueur_a: field(key: "longueur_a") { value }
            largeur_b: field(key: "largeur_b") { value }
            rayon: field(key: "rayon") { value }
            jupe: field(key: "jupe") { value }

            marque: field(key: "marque") {
              reference {
                ... on Metaobject {
                  id
                  handle
                  nom: field(key: "nom") { value }
                  name: field(key: "name") { value }
                  title: field(key: "title") { value }
                  label: field(key: "label") { value }
                }
              }
            }

            # forme = liste de texte (pas une référence)
            forme: field(key: "forme") { value }
          }
        }
      }
    `;
  }

  /**
   * ==============================
   * Storefront API
   * ==============================
   */
  async function storefrontGraphQL(endpoint, token, query, variables) {
    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": token
      },
      body: JSON.stringify({ query, variables })
    });

    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error("HTTP " + res.status + " - " + txt);
    }

    const json = await res.json();

    if (json.errors && json.errors.length) {
      throw new Error(json.errors.map((e) => e.message).join(" | "));
    }

    return json.data;
  }

  async function fetchAllSpaDimensions(endpoint, token) {
    const languageCode = getShopifyLanguageCode();
    const QUERY = buildSpaDimensionsQuery(languageCode);

    const all = [];
    let after = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const data = await storefrontGraphQL(endpoint, token, QUERY, {
        first: 250,
        after: after
      });

      const conn = data && data.metaobjects;
      const nodes = conn && conn.nodes ? conn.nodes : [];
      const pageInfo = conn && conn.pageInfo ? conn.pageInfo : null;

      all.push(...nodes);

      hasNextPage = Boolean(pageInfo && pageInfo.hasNextPage);
      after = pageInfo ? pageInfo.endCursor : null;
    }

    return all;
  }

  /**
   * ==============================
   * Mapping
   * ==============================
   */
  function labelFromRef(ref) {
    if (!ref) return "";
    return (
      (ref.nom && ref.nom.value) ||
      (ref.name && ref.name.value) ||
      (ref.title && ref.title.value) ||
      (ref.label && ref.label.value) ||
      ref.handle ||
      ""
    ).trim();
  }

  function getBrandLabel(raw) {
    const brandRef = raw.marque && raw.marque.reference ? raw.marque.reference : null;
    const brandHandle = (brandRef && brandRef.handle) ? brandRef.handle : "no-brand";
    const brandLabel = labelFromRef(brandRef) || brandHandle;
    return { brandHandle, brandLabel };
  }

  function getFormeLabels(raw) {
    const value = raw.forme && raw.forme.value ? raw.forme.value : "";
    const items = parseListTextValue(value);
    return items.join(", "); // virgule + espace
  }

  function mapEntry(raw) {
    const brand = getBrandLabel(raw);
    const formeLabel = getFormeLabels(raw);

    return {
      id: raw.id || "",
      brandHandle: brand.brandHandle,
      brandLabel: brand.brandLabel,
      modele: (raw.modele && raw.modele.value ? raw.modele.value : "").trim(),
      longueur_a: (raw.longueur_a && raw.longueur_a.value ? raw.longueur_a.value : "").trim(),
      largeur_b: (raw.largeur_b && raw.largeur_b.value ? raw.largeur_b.value : "").trim(),
      rayon: (raw.rayon && raw.rayon.value ? raw.rayon.value : "").trim(),
      jupe: (raw.jupe && raw.jupe.value ? raw.jupe.value : "").trim(),
      forme: formeLabel
    };
  }

  function groupByBrand(entries) {
    const locale = getLocale();
    const map = new Map();

    entries.forEach((e) => {
      const k = e.brandHandle || "no-brand";
      if (!map.has(k)) {
        map.set(k, {
          brandHandle: k,
          brandLabel: e.brandLabel || k,
          rows: []
        });
      }
      map.get(k).rows.push(e);
    });

    const groups = Array.from(map.values()).sort((a, b) =>
      a.brandLabel.localeCompare(b.brandLabel, locale, { sensitivity: "base" })
    );

    groups.forEach((g) => {
      g.rows.sort((r1, r2) =>
        r1.modele.localeCompare(r2.modele, locale, { sensitivity: "base" })
      );
    });

    return groups;
  }

  /**
   * ==============================
   * Render
   * ==============================
   */
  function renderAccordion(root, groups) {
    const t = getI18n(root);

    if (!groups.length) {
      root.innerHTML = "<p>" + esc(t.noData) + "</p>";
      return;
    }

    const html = groups.map((g) => {
      const rowsHtml = g.rows.map((r) => `
        <tr>
          <td>${esc(r.modele)}</td>
          <td>${esc(r.longueur_a)}</td>
          <td>${esc(r.largeur_b)}</td>
          <td>${esc(r.rayon)}</td>
          <td>${esc(r.jupe)}</td>
          <td>${esc(r.forme)}</td>
        </tr>
      `).join("");

      return `
        <accordion-tab class="accordion">
          <details>
            <summary>
              <span class="summary__title">
                <p class="accordion__title h3">${esc(g.brandLabel)}</p>
              </span>
              <span class="icon icon-plus-alt"></span>
            </summary>

            <div class="accordion__content rte typeset">
              <input
                type="text"
                class="spa-search"
                placeholder="${esc(t.searchPlaceholder)}"
                data-brand="${esc(g.brandHandle)}"
              />

              <div class="table-wrap">
                <table class="spa-table" data-brand="${esc(g.brandHandle)}">
                  <thead>
                    <tr>
                      <th data-sort-type="text" class="model">${esc(t.colModel)} <span class="sort-arrow" aria-hidden="true"></span></th>
                      <th data-sort-type="number" class="length">${esc(t.colLength)} <span class="sort-arrow" aria-hidden="true"></span></th>
                      <th data-sort-type="number" class="width">${esc(t.colWidth)} <span class="sort-arrow" aria-hidden="true"></span></th>
                      <th data-sort-type="number" class="radius">${esc(t.colRadius)} <span class="sort-arrow" aria-hidden="true"></span></th>
                      <th data-sort-type="number" class="skirt">${esc(t.colSkirt)} <span class="sort-arrow" aria-hidden="true"></span></th>
                      <th data-sort-type="text" class="shape">${esc(t.colShape)} <span class="sort-arrow" aria-hidden="true"></span></th>
                    </tr>
                  </thead>
                  <tbody>
                    ${rowsHtml}
                  </tbody>
                </table>
              </div>
            </div>
          </details>
        </accordion-tab>
      `;
    }).join("");

    root.innerHTML = `<div class="spa-dimensions-accordion">${html}</div>`;
  }

  /**
   * ==============================
   * Events (search + sort)
   * ==============================
   */
  function bindGlobalEventsOnce() {
    if (document.documentElement.dataset.spaDimensionsBound === "1") return;
    document.documentElement.dataset.spaDimensionsBound = "1";

    // Recherche live
    document.addEventListener("input", function (e) {
      const input = e.target.closest(".spa-search");
      if (!input) return;

      const table = input.closest(".accordion__content")?.querySelector(".spa-table");
      if (!table) return;

      const rows = table.querySelectorAll("tbody tr");
      const query = (input.value || "").toLowerCase();

      rows.forEach((row) => {
        const text = (row.textContent || "").toLowerCase();
        row.style.display = text.includes(query) ? "" : "none";
      });
    });

    // Tri
    document.addEventListener("click", function (e) {
      const th = e.target.closest(".spa-table th");
      if (!th) return;

      const headers = Array.from(th.parentNode.children);
      const anotherSorted = headers.some((h) => h !== th && h.dataset.sortDir);
      const forceAsc = !th.dataset.sortDir && anotherSorted;

      sortTableByTh(th, forceAsc);
    });
  }

  /**
   * ==============================
   * Init
   * ==============================
   */
  async function initSection(sectionRoot, forcedRoot) {
    const root = forcedRoot || sectionRoot.querySelector('[id^="spa-dimensions-root-"], .spa-dimensions-root');
    if (!root) return;

    const t = getI18n(root);

    const shopDomain = root.dataset.shopDomain;
    const token = root.dataset.storefrontToken;
    const apiVersion = root.dataset.apiVersion || "2026-01";

    if (!shopDomain || !token) {
      root.innerHTML = "<p>" + esc(t.configMissing) + "</p>";
      return;
    }

    const endpoint = "https://" + shopDomain + "/api/" + apiVersion + "/graphql.json";

    try {
      root.innerHTML = "<p class='spa-loading'>" + esc(t.loading) + "</p>";

      const raw = await fetchAllSpaDimensions(endpoint, token);
      const mapped = raw.map(mapEntry);
      const groups = groupByBrand(mapped);

      renderAccordion(root, groups);
      applyDefaultSort(root);

      // Debug si besoin:
      // console.log("Lang:", getShopifyLanguageCode(), "Ex formes:", mapped.slice(0, 15).map(x => ({ m: x.modele, f: x.forme })));
    } catch (err) {
      console.error("[spa-dimensions] Erreur:", err);
      root.innerHTML = "<p>" + esc(t.loadError) + "</p>";
    }
  }

  function initAllInDocument(docRoot) {
    bindGlobalEventsOnce();

    // Priorité: root avec ID sectionné
    let roots = Array.from(docRoot.querySelectorAll('[id^="spa-dimensions-root-"]'));

    // Fallback éventuel
    if (!roots.length) {
      roots = Array.from(docRoot.querySelectorAll(".spa-dimensions-root"));
    }

    roots.forEach((rootEl) => {
      const sectionEl = rootEl.closest('[id^="shopify-section-"]') || docRoot;
      initSection(sectionEl, rootEl);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAllInDocument(document);
    });
  } else {
    initAllInDocument(document);
  }

  // Reload section dans l'éditeur de thème
  document.addEventListener("shopify:section:load", function (event) {
    if (!event || !event.target) return;
    initAllInDocument(event.target);
  });
})();
